import { Api, ExternalSearchRequestDto } from '@axiom/ui';
import type {
  Candidate,
  GroupedExperiences,
  AfcSearchFilters,
  LimitedCandidate,
  TalentSearchResponse,
  SavedCandidate,
} from '@axiom/validation';

import { EnvUtil } from '../utils/env-util';
import type { SearchFilters } from '../models/search-filters';

interface StatsFilter {
  search?: string;
  view?: string;
  sort?: string;
}

class CandidateApiClass extends Api {
  constructor() {
    super({
      domain: EnvUtil.apiAltUrl,
      cookieDomain: EnvUtil.cookieDomain,
    });
  }

  readCandidateGroupedExperiences(
    candidateId: Candidate['id'],
    query?: { submissionId: string }
  ) {
    return super.read<{ data: GroupedExperiences[] }>({
      endpoint: `/candidates/${candidateId}/groupedExperiences`,
      query,
      method: 'GET',
    });
  }

  readCandidate(
    candidateId: Candidate['id'],
    query: { isAnonymous?: boolean } = { isAnonymous: false }
  ) {
    return super.read<{ data: Candidate }>({
      endpoint: `/candidates/${candidateId}`,
      method: 'GET',
      query: {
        isAnonymous: query.isAnonymous,
      },
    });
  }

  readCandidateFilters() {
    return super.read<{ data: AfcSearchFilters }>({
      endpoint: '/candidates/search/filters',
      method: 'GET',
    });
  }

  readCandidateStats(body: {
    stats: Array<{
      id: string;
      type: string;
      filters: StatsFilter;
    }>;
  }) {
    return super.read<{ data: Array<{ count: number; id: string }> }>({
      endpoint: '/candidates/stats',
      method: 'POST',
      encodeQuery: false,
      body,
    });
  }

  readCandidateSearchResults(filterMeta: {
    page: number;
    search?: string;
    sort?: string;
    filters: SearchFilters;
    pageSize?: number;
  }) {
    const requestBody = new ExternalSearchRequestDto(filterMeta).export();

    return super.read<TalentSearchResponse>({
      endpoint: '/candidates/search',
      method: 'POST',
      body: requestBody,
    });
  }

  readLimitedCandidate(id: string) {
    return super.read<{ data: LimitedCandidate }>({
      endpoint: `/limitedCandidates/${id}`,
      method: 'GET',
    });
  }

  async refreshCandidateSearchResults() {
    return super.invalidate('/candidates/search');
  }

  readCandidatesSimilarTo(id: Candidate['id']) {
    return super.read<{ data: LimitedCandidate[] }>({
      endpoint: `/candidates/${id}/similarTo`,
      method: 'GET',
      query: {
        pageSize: 9,
        pageNumber: 1,
      },
    });
  }

  async refreshCandidate(candidateId: Candidate['id']) {
    return super.invalidate(`/candidates/${candidateId}`);
  }

  readFavoriteCandidates() {
    return super.read<{ data: SavedCandidate[] }>({
      endpoint: `/user/me/saved/candidates`,
      method: 'GET',
    });
  }

  async refreshFavoriteCandidates() {
    return super.invalidate(`/user/me/saved/candidates`);
  }

  async createSavedCandidate(candidateId: Candidate['id']) {
    return super.write<{ data: Candidate }>({
      method: 'POST',
      endpoint: '/user/me/saved/candidate',
      body: {
        candidates: [candidateId],
      },
    });
  }

  async deleteSavedCandidate(candidateId: Candidate['id']) {
    return super.write<{ data: Candidate }>({
      method: 'DELETE',
      endpoint: `/user/me/saved/candidates/${candidateId}`,
    });
  }
}

export const CandidateApi = new CandidateApiClass();
